import { useEffect, useRef, useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { Toggle } from './toggle'
import { NumericInput } from './numeric-input'
import { HOLDINGS_FILTER, type FilterStore } from '@/lib/holdings-filter'

/**
 * The control on the holdings header: hide balances under a dollar line (John, Oct 30 2026). It sits beside the
 * portfolio total (Nov 4 2026), tucked in by part of the group's gap so the icon reads as the total's companion
 * (and the phone row keeps its width); the popover opens leftward-aligned under the button from sm up; on phones the
 * button is static and the popover hangs from the left edge of the header row, which is positioned for it,
 * since a 288px panel anchored to a button mid-row would run past a phone's right edge. The Kraken card shows the
 * same control with its own store (John, Nov 4 2026).
 */
export function HoldingsSettings({ store = HOLDINGS_FILTER, label = 'Holdings settings' }: { store?: FilterStore; label?: string }) {
  const filter = store.use()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(String(filter.minUsd))
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])
  function toggle() {
    if (!open) setDraft(String(filter.minUsd))
    setOpen(!open)
  }
  function edit(value: string) {
    setDraft(value)
    const amount = Number(value)
    if (value.trim() !== '' && Number.isFinite(amount) && amount >= 0) store.set({ minUsd: amount })
  }
  return (
    <div ref={ref} className="-ml-1.5 sm:relative sm:-ml-2">
      <button
        type="button"
        className="btn btn-ghost !p-2"
        title={label}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        <SlidersHorizontal className="size-5 lg:size-4" />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={label}
          className="card absolute left-4 top-full z-40 mt-1 w-72 max-w-[calc(100vw-2rem)] p-4 shadow-lg shadow-black/40 sm:left-0 sm:mt-2"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">Hide small balances</div>
              <div className="text-xs text-muted">Tokens worth less than this stay out of the list</div>
            </div>
            <Toggle checked={filter.hideSmall} onChange={(hideSmall) => store.set({ hideSmall })} label="Hide small balances" />
          </div>
          <label className={`mt-4 block ${filter.hideSmall ? '' : 'opacity-50'}`}>
            <span className="text-xs text-muted">Hide under</span>
            <span className="mt-1 flex items-center gap-1 rounded-xl bg-surface-2 px-3">
              <span className="text-muted">$</span>
              <NumericInput
                value={draft}
                disabled={!filter.hideSmall}
                onChange={edit}
                className="w-full bg-transparent py-2 tabular-nums outline-none"
              />
            </span>
          </label>
        </div>
      ) : null}
    </div>
  )
}
